// Official turns on the telemetry charts (spec 2026-10-14 §9). Labels always read "Turn N",
// never "T3": new drivers don't know the shorthand.

/** CommonApi GET /public/racedata/corners/{track}/{layout?}: one placed official turn. */
export interface TrackTurn {
  turnNumber: number;
  name?: string | null;
  distanceFromStartM: number;
}

export interface TurnMarker {
  distanceM: number;
  /** "Turn 5", or "Turns 10–11" where two turns share one bend (Spa's Pouhon). */
  label: string;
}

/** Two turns this close (metres) are one bend on the chart. */
const SAME_BEND_M = 1;

/** In lap order, turns sharing a distance merged into one marker. */
export function turnMarkers(turns: TrackTurn[] | null | undefined): TurnMarker[] {
  const sorted = [...(turns ?? [])]
    .filter((t) => Number.isFinite(t.distanceFromStartM) && t.distanceFromStartM >= 0 && t.turnNumber > 0)
    .sort((a, b) => a.distanceFromStartM - b.distanceFromStartM || a.turnNumber - b.turnNumber);

  const groups: TrackTurn[][] = [];
  for (const turn of sorted) {
    const last = groups[groups.length - 1];
    if (last && turn.distanceFromStartM - last[0].distanceFromStartM <= SAME_BEND_M) last.push(turn);
    else groups.push([turn]);
  }

  return groups.map((group) => {
    const numbers = group.map((t) => t.turnNumber);
    const first = Math.min(...numbers);
    const last = Math.max(...numbers);
    return {
      distanceM: group[0].distanceFromStartM,
      label: first === last ? `Turn ${first}` : `Turns ${first}–${last}`,
    };
  });
}

/** Annotation keys this module owns, so the chart hook can keep them when it rebuilds the rest. */
export const TURN_KEY_PREFIX = 'turn_';

/**
 * Chart.js annotation configs for the turns: a faint line per bend, and on a `labelled` chart a
 * "Turn N" label along the top. Labels closer than `minLabelGap` of the lap to the previous
 * label are skipped so they never print on top of each other; the line still shows.
 */
export function turnAnnotations(
  turns: TrackTurn[] | null | undefined,
  trackLength: number,
  labelled: boolean,
  minLabelGap = 0.045,
): Record<string, object> {
  const out: Record<string, object> = {};
  const gapM = Math.max(0, trackLength) * minLabelGap;
  let lastLabelledAt = Number.NEGATIVE_INFINITY;

  turnMarkers(turns).forEach((marker, i) => {
    if (trackLength > 0 && marker.distanceM > trackLength) return;
    const showLabel = labelled && marker.distanceM - lastLabelledAt >= gapM;
    if (showLabel) lastLabelledAt = marker.distanceM;
    out[`${TURN_KEY_PREFIX}${i}`] = {
      type: 'line',
      xMin: marker.distanceM,
      xMax: marker.distanceM,
      borderColor: 'rgba(255, 255, 255, 0.16)',
      borderWidth: 1,
      borderDash: [2, 3],
      label: {
        display: showLabel,
        content: marker.label,
        position: 'end',
        color: '#c8c8c8',
        backgroundColor: 'rgba(0, 0, 0, 0.65)',
        padding: 3,
        font: { size: 10, family: "'Roboto Condensed', sans-serif" },
      },
    };
  });

  return out;
}
